import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Lock, KeyRound } from "lucide-react";

const LOCK_OPTIONS = [
  { value: 0, label: "Off — stay signed in" },
  { value: 30, label: "After 30 minutes" },
  { value: 360, label: "After 6 hours" },
  { value: 720, label: "After 12 hours" },
  { value: 1440, label: "After 24 hours" },
];

export function SecuritySection() {
  const { user, refresh } = useAuth();
  const [lockMins, setLockMins] = useState(user?.pin_lock_minutes || 0);
  const [pin, setPin] = useState("");
  const [pinMsg, setPinMsg] = useState(null); // {ok, text}
  const [saving, setSaving] = useState(false);

  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  const savePin = async () => {
    setPinMsg(null);
    if (lockMins > 0) {
      const needsPin = !user?.pin_enabled || pin.length > 0;
      if (needsPin && !/^\d{4,6}$/.test(pin)) {
        setPinMsg({ ok: false, text: "Enter a 4–6 digit PIN" });
        return;
      }
    }
    setSaving(true);
    try {
      await api.post("/auth/pin", { pin: pin || null, lock_minutes: lockMins });
      await refresh();
      setPin("");
      setPinMsg({ ok: true, text: lockMins === 0 ? "PIN lock disabled" : "PIN lock saved" });
    } catch (e) {
      setPinMsg({ ok: false, text: e.response?.data?.detail || "Could not save" });
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    setPwMsg(null);
    if (nw.length < 6) { setPwMsg({ ok: false, text: "New password must be at least 6 characters" }); return; }
    if (nw !== cf) { setPwMsg({ ok: false, text: "Passwords do not match" }); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      setCur(""); setNw(""); setCf("");
      setPwMsg({ ok: true, text: "Password updated" });
    } catch (e) {
      setPwMsg({ ok: false, text: e.response?.data?.detail || "Could not update password" });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <>
      <section className="hud-frame p-6 space-y-5" data-testid="settings-pin-lock">
        <div className="kpi-label flex items-center gap-2"><Lock size={11} /> PIN LOCK</div>
        <p className="text-sm text-muted-foreground">
          You stay signed in for up to 7 days. Optionally, require a quick PIN after a period of
          inactivity — no full re-login needed.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="kpi-label">AUTO-LOCK</label>
            <select
              value={lockMins}
              onChange={(e) => setLockMins(Number(e.target.value))}
              className="hud-input mt-2 w-full"
              data-testid="settings-pin-interval"
            >
              {LOCK_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {lockMins > 0 && (
            <div>
              <label className="kpi-label">{user?.pin_enabled ? "CHANGE PIN (OPTIONAL)" : "SET 4–6 DIGIT PIN"}</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder={user?.pin_enabled ? "Keep current PIN" : "e.g. 4820"}
                className="hud-input mt-2 w-full mono tracking-[0.35em]"
                data-testid="settings-pin-input"
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={savePin} disabled={saving} className="hud-btn hud-btn-primary" data-testid="settings-pin-save">
            {saving ? "SAVING…" : "SAVE PIN LOCK"}
          </button>
          {pinMsg && (
            <span className={`mono text-[11px] uppercase tracking-widest ${pinMsg.ok ? "text-oud" : "text-[hsl(var(--oxblood))]"}`} data-testid="settings-pin-msg">
              {pinMsg.text}
            </span>
          )}
        </div>
      </section>

      <section className="hud-frame p-6 space-y-5" data-testid="settings-change-password">
        <div className="kpi-label flex items-center gap-2"><KeyRound size={11} /> CHANGE PASSWORD</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <input
            type="password"
            value={cur}
            onChange={(e) => setCur(e.target.value)}
            placeholder="Current password"
            className="hud-input"
            data-testid="settings-current-password"
          />
          <input
            type="password"
            value={nw}
            onChange={(e) => setNw(e.target.value)}
            placeholder="New password"
            className="hud-input"
            data-testid="settings-new-password"
          />
          <input
            type="password"
            value={cf}
            onChange={(e) => setCf(e.target.value)}
            placeholder="Confirm new password"
            className="hud-input"
            data-testid="settings-confirm-password"
          />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={savePassword} disabled={pwSaving} className="hud-btn" data-testid="settings-password-save">
            {pwSaving ? "UPDATING…" : "UPDATE PASSWORD"}
          </button>
          {pwMsg && (
            <span className={`mono text-[11px] uppercase tracking-widest ${pwMsg.ok ? "text-oud" : "text-[hsl(var(--oxblood))]"}`} data-testid="settings-password-msg">
              {pwMsg.text}
            </span>
          )}
        </div>
      </section>
    </>
  );
}
